"use client"

import React, { useEffect, useState } from "react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/components/tooltip"

interface ABTastyTestClientProps {
  testId: string
  variantAContent: React.ReactNode
  variantBContent: React.ReactNode
  showDebug?: boolean
  forceVariant?: "AUTO" | "VARIANT_A" | "VARIANT_B"
}

export default function ABTastyTestClient({
  testId,
  variantAContent,
  variantBContent,
  showDebug,
  forceVariant,
}: ABTastyTestClientProps) {
  // 0 = Loading/Default, 1 = Variante A, 2 = Variante B
  const [activeVariant, setActiveVariant] = useState<number | null>(null)
  const [abTastyResults, setAbTastyResults] = useState<any>(null)

  useEffect(() => {
    // Wenn force_variant gesetzt ist, direkt diese Variante verwenden
    if (forceVariant === "VARIANT_A") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveVariant(1)
      return
    }
    if (forceVariant === "VARIANT_B") {
      setActiveVariant(2)
      return
    }

    // Funktion zur Abfrage der AB Tasty Daten
    const checkABTasty = () => {
      //console.log("Check ABTasty")
      // Das ABTasty Objekt wird vom synchronen Script im Head injiziert
      if (typeof window !== "undefined" && (window as any).ABTasty) {
        try {
          //console.log("ABTasty found")
          const results = (window as any).ABTasty.results //Die results aus ABTasty
          setAbTastyResults(results)
          //console.log("ABTasty results:")
          //console.dir(results)
          if (testId && results && results[testId]) {
            const result = results[testId]

            const variationID = result.variationID

            //console.log("ABTasty variationID: " + variationID)
            if (variationID === 0) {
              setActiveVariant(1)
            } else {
              setActiveVariant(2)
            }
            return true
          }
        } catch (e) {
          console.error("Fehler beim Lesen von AB Tasty:", e)
        }
      }
      return false
    }

    // Da das Script synchron ist, ist es oft sofort da.
    // Dennoch nutzen wir ein kurzes Intervall für maximale Sicherheit.
    const isFound = checkABTasty()
    if (!isFound) {
      const interval = setInterval(() => {
        if (checkABTasty()) clearInterval(interval)
      }, 50)

      // Timeout nach 2 Sekunden, um nicht ewig zu warten (Fallback)
      setTimeout(() => {
        clearInterval(interval)
        if (activeVariant === null) setActiveVariant(1) // Fallback auf A
      }, 2000)
    }
  }, [activeVariant, testId, forceVariant])

  // Solange AB Tasty entscheidet: Zeige nichts
  if (activeVariant === null) {
    return <div />
  }

  const isForced = forceVariant === "VARIANT_A" || forceVariant === "VARIANT_B"

  return (
    <div className={"relative"}>
      {showDebug && (
        <div className="absolute top-2 right-2 z-50">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="
                  flex h-6 w-6 items-center justify-center rounded-full bg-gray-700 text-sm font-bold text-white
                  transition-colors
                  hover:bg-gray-600
                "
              >
                ?
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-h-96 w-80 overflow-auto bg-gray-900 text-left text-white">
              <div className="mb-2 font-bold">ABTasty Debug Info</div>
              <div className="mb-2">
                <span className="text-gray-400">Test ID:</span> {testId}
              </div>
              <div className="mb-2">
                <span className="text-gray-400">Active Variant:</span>{" "}
                {activeVariant === 1 ? "A" : activeVariant === 2 ? "B" : "Loading..."}
                {isForced && <span className="ml-1 text-yellow-400">(forced)</span>}
              </div>
              {isForced && <div className="mb-2 text-yellow-400">Force Variant: {forceVariant}</div>}
              <div className="mt-2 border-t border-gray-700 pt-2">
                <div className="mb-1 text-gray-400">ABTasty Results:</div>
                <pre className="wrap-break-word whitespace-pre-wrap">
                  {abTastyResults ? JSON.stringify(abTastyResults, null, 2) : "Not loaded"}
                </pre>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      {activeVariant === 1 ? <>{variantAContent}</> : <>{variantBContent}</>}
    </div>
  )
}
